"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

type ForgotPasswordResponse = {
  success?: boolean;
  emailed?: boolean;
  expiresInMinutes?: number;
  error?: string;
};

export default function ForgotPasswordClient() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ email: string; expiresInMinutes: number } | null>(null);
  // Issue 33.2 — surface a per-click cooldown so users don't hammer the
  // button when they don't see the email immediately. 60 seconds matches
  // the server's send-rate limit.
  const [cooldownSec, setCooldownSec] = useState(0);

  useEffect(() => {
    if (cooldownSec <= 0) return;
    const t = setTimeout(() => setCooldownSec((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldownSec]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    // Issue 33.1 — client-side validation instead of round-tripping for
    // obvious empty input. Email field is type="email" + required which
    // the browser will catch, but the form has noValidate so add a JS
    // guard too. Show the same "Enter your email" copy whether the
    // value is empty or fails the simple shape check.
    const trimmed = email.trim();
    if (!trimmed) {
      setError("Enter your email address.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Enter a valid email address.");
      return;
    }
    if (cooldownSec > 0) {
      setError(`Please wait ${cooldownSec}s before requesting another link.`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      const data = (await res.json().catch(() => ({}))) as ForgotPasswordResponse;

      if (!res.ok || data.error) {
        setError(data.error ?? "Something went wrong. Please try again.");
        return;
      }
      if (data.emailed || data.success) {
        setSent({ email: trimmed, expiresInMinutes: data.expiresInMinutes ?? 30 });
        setCooldownSec(60);
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div className="auth-column">
        <h1 className="auth-headline">Reset link sent.</h1>
        <p className="auth-subline">Check your email.</p>

        <p className="auth-body-text">
          If an account exists for{" "}
          <strong>{sent.email}</strong>, we sent a
          secure link to reset your password. The link expires in{" "}
          {sent.expiresInMinutes} minutes.
        </p>
        <p className="auth-body-text-muted">
          Didn&rsquo;t get it? Check your spam folder, or{" "}
          <button
            type="button"
            onClick={() => setSent(null)}
            className="auth-link"
          >
            try a different email
          </button>
          .
        </p>

        <div className="auth-switch">
          Remember it? <Link href="/login">Back to log in</Link>
        </div>

        <div className="auth-footnote">
          Secure link <span className="auth-footnote-sep">·</span> Expires in {sent.expiresInMinutes} minutes
        </div>
      </div>
    );
  }

  return (
    <div className="auth-column">
      <h1 className="auth-headline">Reset your password.</h1>
      <p className="auth-subline">No password? No problem.</p>

      <form onSubmit={handleSubmit} noValidate>
        <div className="auth-field">
          <label htmlFor="email" className="auth-label">Email</label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            spellCheck={false}
            className="auth-input"
          />
          <p className="auth-hint">
            Enter the email on your account. We&rsquo;ll send a secure link to set a
            new password.
          </p>
        </div>

        {error && (
          <div className="auth-alert" role="alert">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="auth-submit"
        >
          {submitting ? "Sending link…" : "Email me a reset link"}{" "}
          <span aria-hidden="true">→</span>
        </button>
      </form>

      <div className="auth-switch">
        Remember it? <Link href="/login">Back to log in</Link>
      </div>

      <div className="auth-footnote">
        Secure link <span className="auth-footnote-sep">·</span> Expires in 30 minutes
      </div>
    </div>
  );
}
